// Admin Camp Management Component
// Manage camps in camp_audit_data table with CRUD operations and CSV import/export

import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2,
  Download,
  Upload,
  FileSpreadsheet,
  X,
  Loader2
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { exportCampsToCSV, downloadCSV, importCampsFromCSV, validateCSVFile } from '@/services/campImportExportService';
import { isAvailable as isGoogleSheetsAvailable } from '@/services/googleSheetsService';

const AdminCampManagement = () => {
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [fieldConfig, setFieldConfig] = useState([]);
  const [formData, setFormData] = useState({});
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0,
    hasMore: false
  });
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  useEffect(() => {
    loadFieldConfig();
    loadCamps();
  }, []);

  useEffect(() => {
    loadCamps();
  }, [search, categoryFilter, sourceFilter, pagination.offset]);

  const loadFieldConfig = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch('/api/admin/camps/fields', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFieldConfig(data.fields);
        }
      }
    } catch (error) {
      console.error('Error loading field config:', error);
    }
  };

  const loadCamps = async () => {
    try {
      setLoading(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }
      
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: pagination.offset.toString(),
        search: search || '',
        category: categoryFilter || '',
        source: sourceFilter || ''
      });
      
      const response = await fetch(`/api/admin/camps?${params}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || `Failed to load camps: ${response.status}`;
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setCamps(data.camps);
        setPagination(data.pagination);
      } else {
        throw new Error(data.message || 'Failed to load camps');
      }
    } catch (error) {
      console.error('Error loading camps:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load camps',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedCamp(null);
    setFormData({});
    setIsFormModalOpen(true);
  };

  const handleEdit = async (camp) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`/api/admin/camps/${camp.id}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSelectedCamp(data.camp);
          setFormData(data.camp);
          setIsFormModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Error loading camp:', error);
      toast({
        title: 'Error',
        description: 'Failed to load camp details',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (camp) => {
    if (!confirm(`Are you sure you want to delete "${camp.name}"?`)) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`/api/admin/camps/${camp.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Camp deleted successfully'
        });
        loadCamps();
      } else {
        throw new Error('Failed to delete camp');
      }
    } catch (error) {
      console.error('Error deleting camp:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete camp',
        variant: 'destructive'
      });
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.name) {
        toast({
          title: 'Validation Error',
          description: 'Camp name is required',
          variant: 'destructive'
        });
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const url = selectedCamp 
        ? `/api/admin/camps/${selectedCamp.id}`
        : '/api/admin/camps';
      
      const method = selectedCamp ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Success',
          description: selectedCamp ? 'Camp updated successfully' : 'Camp created successfully'
        });
        setIsFormModalOpen(false);
        setFormData({});
        setSelectedCamp(null);
        loadCamps();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save camp');
      }
    } catch (error) {
      console.error('Error saving camp:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save camp',
        variant: 'destructive'
      });
    }
  };

  const handleExport = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get selected fields or all editable fields
      const fieldsToExport = fieldConfig
        .filter(f => f.editable !== false)
        .map(f => f.key);

      const filters = {
        search: search || '',
        category: categoryFilter || '',
        source: sourceFilter || ''
      };

      const response = await fetch('/api/admin/camps/export', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fields: fieldsToExport, filters })
      });

      if (!response.ok) {
        throw new Error('Failed to export camps');
      }

      const blob = await response.blob();
      downloadCSV(blob);
      
      toast({
        title: 'Success',
        description: 'Camps exported successfully'
      });
      setIsExportModalOpen(false);
    } catch (error) {
      console.error('Error exporting camps:', error);
      toast({
        title: 'Error',
        description: 'Failed to export camps',
        variant: 'destructive'
      });
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast({
        title: 'Error',
        description: 'Please select a CSV file',
        variant: 'destructive'
      });
      return;
    }

    const validation = validateCSVFile(importFile);
    if (!validation.isValid) {
      toast({
        title: 'Validation Error',
        description: validation.errors.join(', '),
        variant: 'destructive'
      });
      return;
    }

    try {
      setImporting(true);
      const results = await importCampsFromCSV(importFile);
      setImportResults(results);
      
      toast({
        title: 'Import Complete',
        description: `Imported ${results.successful} camps, ${results.failed} failed`
      });
      
      if (results.successful > 0) {
        loadCamps();
      }
    } catch (error) {
      console.error('Error importing camps:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to import camps',
        variant: 'destructive'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setImportResults(null);
    }
  };

  // Group fields by category
  const fieldsByCategory = useMemo(() => {
    const grouped = {};
    fieldConfig.forEach(field => {
      if (!grouped[field.category]) {
        grouped[field.category] = [];
      }
      grouped[field.category].push(field);
    });
    return grouped;
  }, [fieldConfig]);

  const renderFormField = (field) => {
    const value = formData[field.key] ?? field.defaultValue ?? '';
    const isReadOnly = field.editable === false;

    switch (field.type) {
      case 'textarea':
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={field.key}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              id={field.key}
              value={value}
              onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
              disabled={isReadOnly}
              rows={3}
            />
          </div>
        );
      
      case 'number':
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={field.key}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={field.key}
              type="number"
              value={value}
              onChange={(e) => setFormData({ ...formData, [field.key]: parseFloat(e.target.value) || null })}
              disabled={isReadOnly}
            />
          </div>
        );
      
      case 'date':
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={field.key}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={field.key}
              type="date"
              value={value ? value.split('T')[0] : ''}
              onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
              disabled={isReadOnly}
            />
          </div>
        );
      
      case 'url':
      case 'email':
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={field.key}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={field.key}
              type={field.type}
              value={value}
              onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
              disabled={isReadOnly}
            />
          </div>
        );
      
      default:
        return (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={field.key}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={field.key}
              type="text"
              value={value}
              onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
              disabled={isReadOnly}
            />
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Camp Management</h2>
          <p className="text-muted-foreground">Manage camps in the database</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCreate} variant="default">
            <Plus className="w-4 h-4 mr-2" />
            Add Camp
          </Button>
          <Button onClick={() => setIsExportModalOpen(true)} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={() => setIsImportModalOpen(true)} variant="outline">
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          {isGoogleSheetsAvailable() && (
            <Button disabled variant="outline" title="Coming soon">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Google Sheets
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search camps..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={categoryFilter || "all"} onValueChange={(val) => setCategoryFilter(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="sports">Sports</SelectItem>
            <SelectItem value="arts">Arts</SelectItem>
            <SelectItem value="technology">Technology</SelectItem>
            <SelectItem value="outdoor">Outdoor</SelectItem>
            <SelectItem value="academic">Academic</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sourceFilter || "all"} onValueChange={(val) => setSourceFilter(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="admin_manual">Admin Manual</SelectItem>
            <SelectItem value="admin_import">Admin Import</SelectItem>
            <SelectItem value="google_places_api">Google Places</SelectItem>
            <SelectItem value="ai_discovery">AI Discovery</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Camp List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {camps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No camps found
                    </TableCell>
                  </TableRow>
                ) : (
                  camps.map((camp) => (
                    <TableRow key={camp.id}>
                      <TableCell className="font-medium">{camp.name || 'Unnamed'}</TableCell>
                      <TableCell>{camp.location || camp.formatted_address || '-'}</TableCell>
                      <TableCell>
                        {camp.primary_category ? (
                          <Badge variant="secondary">{camp.primary_category}</Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{camp.source || 'unknown'}</Badge>
                      </TableCell>
                      <TableCell>
                        {camp.audit_date 
                          ? new Date(camp.audit_date).toLocaleDateString()
                          : camp.created_at
                          ? new Date(camp.created_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(camp)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(camp)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} camps
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination({ ...pagination, offset: Math.max(0, pagination.offset - pagination.limit) })}
                  disabled={pagination.offset === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination({ ...pagination, offset: pagination.offset + pagination.limit })}
                  disabled={!pagination.hasMore}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Camp Form Modal */}
      <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedCamp ? 'Edit Camp' : 'Create New Camp'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <Tabs defaultValue={Object.keys(fieldsByCategory)[0]} className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                {Object.keys(fieldsByCategory).map(category => (
                  <TabsTrigger key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </TabsTrigger>
                ))}
              </TabsList>
              {Object.entries(fieldsByCategory).map(([category, fields]) => (
                <TabsContent key={category} value={category} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    {fields.map(field => renderFormField(field))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {selectedCamp ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <Dialog open={isExportModalOpen} onOpenChange={setIsExportModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Camps to CSV</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Export all camps matching current filters to CSV format.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExportModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Camps from CSV</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="csv-file">Select CSV File</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="mt-2"
              />
            </div>
            {importFile && (
              <div className="flex items-center gap-2 text-sm">
                <FileSpreadsheet className="w-4 h-4" />
                <span>{importFile.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setImportFile(null);
                    setImportResults(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            {importResults && (
              <div className="space-y-2 p-4 bg-muted rounded-lg">
                <div className="text-sm font-medium">Import Results:</div>
                <div className="text-sm text-green-600">
                  Successful: {importResults.successful}
                </div>
                <div className="text-sm text-red-600">
                  Failed: {importResults.failed}
                </div>
                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="mt-2">
                    <div className="text-sm font-medium">Errors:</div>
                    <ul className="text-xs text-red-600 list-disc list-inside">
                      {importResults.errors.slice(0, 5).map((error, idx) => (
                        <li key={idx}>
                          Row {error.row}: {error.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsImportModalOpen(false);
              setImportFile(null);
              setImportResults(null);
            }}>
              Close
            </Button>
            <Button onClick={handleImport} disabled={!importFile || importing}>
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCampManagement;

